import React from "react"

import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { useRecoilState, useRecoilValue } from "recoil"
import styled from "styled-components"

import { ConfirmModal } from "~/components/ConfirmModal"
import { usePortal } from "~/components/ui"
import { setupStepsIndex } from "~/containers/Proposal/Setup/Form/setup-steps-index"
import { useDesignContext } from "~/containers/Proposal/state/designs/design-context"
import { designDiffState } from "~/containers/Proposal/state/designs/design-state"
import { brochureJumpToSlideState } from "~/containers/Proposal/state/jump-to-slide-state"
import { proposalRemoteState } from "~/containers/Proposal/state/proposal/proposal-state"
import { getPowurUrl } from "~/utils/getUrlForEnvironment"

import { ReactComponent as BackArrow } from "@svg/tools/back-arrow.svg"

import { useDesignerCapabilities } from "../designer-capabilities"

export const StyledBackLink = styled.div`
  text-decoration: none;
  color: var(--blue-dark-font);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 13px;
  cursor: pointer;
  svg {
    width: 18px;
    transform: translateY(1px);
  }
  span {
    color: var(--blue-dark-font);
    font-weight: 600;
    font-size: 16px;
    text-align: center;
  }
`

const BackLink_Internal: React.FC = () => {
  const { slug } = useRecoilValue(proposalRemoteState)
  const design = useDesignContext()
  const { t } = useTranslation()
  const { id } = setupStepsIndex[`system-placement`].parts[0]
  const backLinkUrl = `/proposal/${slug}/setup/system-placement/${id}`
  const { Portal, show, hide } = usePortal({
    elementId: `confirm-navigate-back`,
  })
  const navigate = useNavigate()
  const [jumpTo, setJumpTo] = useRecoilState(brochureJumpToSlideState)
  const designDiff = useRecoilValue(designDiffState)
  const { pathname } = useLocation()
  const { backTarget } = useDesignerCapabilities()
  const isServiceBureauRoute =
    backTarget.kind === `v1-proposal` && pathname.includes(`service-queue`)
  const handleContinue = () => {
    if (designDiff !== null) {
      design.resetLocal()
    }
    if (backTarget.kind === `custom`) {
      backTarget.navigate()

      return
    }
    if (isServiceBureauRoute) {
      window.location.assign(`${getPowurUrl()}/admin-solar-proposals/vision`)
    }
    if (!jumpTo) {
      navigate(backLinkUrl)
    } else {
      setJumpTo(`systemSummary`)
      navigate(`/proposal/${slug}`)
    }
  }

  return (
    <>
      <StyledBackLink
        data-testid="back-to-proposal"
        onClick={() => (designDiff !== null ? show() : handleContinue())}
      >
        <BackArrow />
        <span>
          {backTarget.kind === `custom`
            ? backTarget.label
            : isServiceBureauRoute
              ? t(`backToQueue`)
              : t(`backToProposal`)}
        </span>
      </StyledBackLink>
      <Portal>
        <ConfirmModal
          Heading={() => <h4>{t(`continueConfirmDesc`)}</h4>}
          onClose={() => hide()}
          onSubmit={handleContinue}
        />
      </Portal>
    </>
  )
}

export const BackLink = React.memo(BackLink_Internal)
